import { z } from "zod";
import { momentSchema } from "./zod-utils";
import { TicketCustomFieldsDataSchema } from "./custom-field";

export const TICKET_STATUSES = ["open", "inProgress", "done", "cancelled"] as const;
export const TICKET_URGENCY_LEVELS = ["low", "medium", "high"] as const;

export const TicketStatusSchema = z.enum(TICKET_STATUSES);
export const TicketUrgencySchema = z.enum(TICKET_URGENCY_LEVELS);

export type TicketStatus = z.infer<typeof TicketStatusSchema>;
export type TicketUrgency = z.infer<typeof TicketUrgencySchema>;

export const TICKET_STATUS = TicketStatusSchema.enum;
export const TICKET_URGENCY = TicketUrgencySchema.enum;

export const TICKET_ACTOR_TYPES = ["user", "serviceProvider"] as const;
export const TicketActorTypeSchema = z.enum(TICKET_ACTOR_TYPES);
export type TicketActorType = z.infer<typeof TicketActorTypeSchema>;
export const TICKET_ACTOR_TYPE = TicketActorTypeSchema.enum;

const TicketUserActorInfoSchema = z.object({
  type: z.literal(TICKET_ACTOR_TYPE.user),
  userUuid: z.uuid(),
  firstName: z.string(),
  lastName: z.string(),
  profileImage: z.string().nullable(),
  avatarColor: z.string(),
});

const TicketServiceProviderActorInfoSchema = z.object({
  type: z.literal(TICKET_ACTOR_TYPE.serviceProvider),
  email: z.email(),
});

export const TicketActorInfoSchema = z.discriminatedUnion("type", [
  TicketUserActorInfoSchema,
  TicketServiceProviderActorInfoSchema,
]);

export type TicketActorInfo = z.infer<typeof TicketActorInfoSchema>;
export type TicketUserActorInfo = z.infer<typeof TicketUserActorInfoSchema>;
export type TicketServiceProviderActorInfo = z.infer<typeof TicketServiceProviderActorInfoSchema>;

const ticketUpdateFields = ["status", "urgency", "categoryUuid"] as const;
export const TicketUpdateFieldSchema = z.enum(ticketUpdateFields);
export const TICKET_UPDATE_FIELD = TicketUpdateFieldSchema.enum;
export type TicketUpdateField = z.infer<typeof TicketUpdateFieldSchema>;

const TicketUpdateCommonShape = {
  uuid: z.uuid(),
  ticketUuid: z.uuid(),
  by: TicketActorInfoSchema,
};

export const TicketStatusUpdateSchema = z.object({
  ...TicketUpdateCommonShape,
  field: z.literal(TICKET_UPDATE_FIELD.status),
  oldValue: TicketStatusSchema,
  newValue: TicketStatusSchema,
  createdAt: momentSchema,
});

export const TicketUrgencyUpdateSchema = z.object({
  ...TicketUpdateCommonShape,
  field: z.literal(TICKET_UPDATE_FIELD.urgency),
  oldValue: TicketUrgencySchema,
  newValue: TicketUrgencySchema,
  createdAt: momentSchema,
});

export const TicketCategoryUpdateSchema = z.object({
  ...TicketUpdateCommonShape,
  field: z.literal(TICKET_UPDATE_FIELD.categoryUuid),
  oldValue: z.uuid(),
  newValue: z.uuid(),
  createdAt: momentSchema,
});

export const TicketUpdateSchema = z.discriminatedUnion("field", [
  TicketStatusUpdateSchema,
  TicketUrgencyUpdateSchema,
  TicketCategoryUpdateSchema,
]);

export type TicketStatusUpdate = z.infer<typeof TicketStatusUpdateSchema>;
export type TicketUrgencyUpdate = z.infer<typeof TicketUrgencyUpdateSchema>;
export type TicketCategoryUpdate = z.infer<typeof TicketCategoryUpdateSchema>;
export type TicketUpdate = z.infer<typeof TicketUpdateSchema>;

export const TicketReporterInfoSchema = z.object({
  userUuid: z.uuid(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  profileImage: z.string().nullable(),
  avatarColor: z.string(),
});

export type TicketReporterInfo = z.infer<typeof TicketReporterInfoSchema>;

export const TicketBaseShape = {
  uuid: z.uuid(),
  code: z.string(),
  externalId: z.string().nullable(),
  status: TicketStatusSchema,
  companyUuid: z.uuid(),
  officeUuid: z.uuid(),
  floorUuid: z.uuid(),
  zoneUuid: z.uuid(),
  resourceUuid: z.uuid().nullable(),
  locationInfo: z.string().nullable(),
  categoryUuid: z.uuid(),
  urgency: TicketUrgencySchema,
  customFieldsData: TicketCustomFieldsDataSchema,
};

export const TicketSchema = z.object({
  ...TicketBaseShape,
  createdAt: momentSchema,
  updatedAt: momentSchema,
  updates: z.array(TicketUpdateSchema).optional(),
  reportedBy: TicketReporterInfoSchema,
});

export type Ticket = z.infer<typeof TicketSchema>;

export type TicketWithDetails = Ticket & { updates: TicketUpdate[] };

export const TICKET_OPEN_STATUSES: readonly TicketStatus[] = [TICKET_STATUS.open, TICKET_STATUS.inProgress];
export const TICKET_COMPLETED_STATUSES: readonly TicketStatus[] = [TICKET_STATUS.done, TICKET_STATUS.cancelled];
